// Pure, dependency-free FDA recall logic. Imported by BOTH the browser app (src/lib/recalls.js
// re-exports this file) and the check-fda-recalls edge function (Deno) — there is exactly one
// copy. Keep it free of imports and of browser/Deno-only globals (URL and Date are fine).

// Includes placeholder names (FDA's Brand field is often "N/A"/"None"/"Various") so two
// placeholders can never "match" each other.
const FIRM_STOP = new Set(['rx', 'pharmacy', 'pharmacies', 'pharmaceutical', 'pharmaceuticals', 'compounding', 'llc', 'inc', 'co', 'corp', 'corporation', 'ltd', 'the', 'and', 'of', 'na', 'none', 'unknown', 'various', 'multiple', 'generic']);
// Form and salt words ("Estradiol Cypionate" vs "Testosterone Cypionate") must not decide a match.
const SUBSTANCE_STOP = new Set(['injection', 'injectable', 'solution', 'vial', 'vials', 'tablet', 'tablets', 'capsule', 'capsules', 'compounded', 'sterile', 'multidose', 'dose', 'single', 'for', 'and', 'with', 'the', 'oral', 'subcutaneous', 'blend',
  'acetate', 'cypionate', 'enanthate', 'propionate', 'sodium', 'chloride', 'hcl', 'hydrochloride', 'phosphate', 'citrate', 'sulfate', 'acid', 'extract', 'powder', 'supplement', 'dietary']);

const words = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
const hasPhrase = (hay, needle) => !!needle && ` ${hay} `.includes(` ${needle} `);

export function normalizeFirm(s) {
  return words(s).filter(w => w.length >= 2 && !FIRM_STOP.has(w)).join(' ');
}

// Either name may be the longer one ("Greenwich" vs FDA's "Greenwich Rx"), so containment is
// checked both ways, on whole words. A name that normalizes to nothing never matches.
export function firmMatches(vialPharmacy, recall) {
  const p = normalizeFirm(vialPharmacy);
  if (!p) return false;
  return [recall?.firm, recall?.brand].some(f => {
    const n = normalizeFirm(f);
    return !!n && (hasPhrase(n, p) || hasPhrase(p, n));
  });
}

const isSubstanceWord = w => w.length >= 3 && !SUBSTANCE_STOP.has(w) && !/^\d+$/.test(w) && !/^\d+(mg|ml|mcg|iu|units?)$/.test(w) && !['mg', 'ml', 'mcg'].includes(w);

export function substanceTokens(compound, vial) {
  const blend = (vial?.blend && vial.blend.length ? vial.blend : compound?.blend) || [];
  const names = [compound?.label, compound?.shortLabel, ...blend.map(i => i?.name)];
  return [...new Set(names.flatMap(words).filter(isSubstanceWord))];
}

// Users name stack items with short forms; FDA writes the full or chemical name.
const SUBSTANCE_ALIASES = { b12: ['cyanocobalamin', 'methylcobalamin', 'hydroxocobalamin'], nad: ['nicotinamide'] };

// Whole-word match, OR a 4+ letter token as a word prefix ("sema" → "semaglutide"), OR an alias.
export function substanceMatches(compound, vial, recall) {
  const hayWords = words(`${recall?.product || ''} ${recall?.title || ''}`);
  const hay = hayWords.join(' ');
  return substanceTokens(compound, vial).some(t => hasPhrase(hay, t)
    || (t.length >= 4 && hayWords.some(w => w.startsWith(t)))
    || (SUBSTANCE_ALIASES[t] || []).some(a => hayWords.includes(a)));
}

// Strips a leading "#", "Lot", "Lot #", "LOT NUMBER:" label — the Lot label only when followed by a
// space, # or :, so a lot that itself starts with LOT (e.g. "LOT-2024-A01") is left intact.
export function normalizeLot(s) {
  return String(s ?? '').toUpperCase().replace(/^\s*#\s*/, '').replace(/^\s*LOT(\s*(NUMBER|NO\.?))?(\s*[#:]+\s*|\s+)/, '').replace(/\s+/g, '');
}

// Lots are compared on letters and digits only, so "0060 072026-1." equals "0060-072026-1".
const lotKey = s => normalizeLot(s).replace(/[^A-Z0-9]/g, '');

export function matchRecall(vial, compound, recall) {
  if (!vial || !recall || !firmMatches(vial.pharmacy, recall)) return null;
  const lots = (recall.lots || []).filter(l => lotKey(l?.lot));
  const key = lotKey(vial.lot);
  if (key && lots.length) {
    // The firm plus an exact lot is conclusive on its own: a short or nicknamed stack item
    // ("Sema", "B12") must never hide a vial whose lot number is on the list.
    const hit = lots.find(l => lotKey(l.lot) === key);
    return hit ? { match: 'lot', bud: hit.bud || null } : null;
  }
  if (!compound || !substanceMatches(compound, vial, recall)) return null;
  return { match: 'possible', bud: null };
}

export const isFinishedVial = v => v?.status === 'empty' || v?.status === 'archived';

export function matchAll(stored, recalls) {
  const out = [];
  for (const [compoundKey, vials] of Object.entries(stored?.vials || {})) {
    const compound = stored?.compounds?.[compoundKey];
    for (const vial of vials || []) {
      for (const recall of recalls || []) {
        const m = matchRecall(vial, compound, recall);
        if (m) out.push({ compoundKey, vialId: vial.id, recall, match: m.match, bud: m.bud, finished: isFinishedVial(vial) });
      }
    }
  }
  return out;
}

export function strongestMatchForVial(matches, vialId) {
  const mine = (matches || []).filter(m => m.vialId === vialId);
  return mine.find(m => m.match === 'lot') || mine[0] || null;
}

// Most shots carry no `vial` (pens/pills never record one; draw types only with 2+ vials), so a
// dose is attributed to the vial opened most recently on or before the dose date.
export function vialForShotAtDate(vials, shot) {
  const vs = vials || [];
  if (shot?.vial) {
    const exact = vs.find(v => v.id === shot.vial);
    if (exact) return exact;
  }
  const day = String(shot?.date || '').slice(0, 10);
  const opened = vs
    .filter(v => v.openedAt && String(v.openedAt).slice(0, 10) <= day)
    .sort((a, b) => String(b.openedAt).localeCompare(String(a.openedAt)));
  if (opened.length) return opened[0];
  return vs.length === 1 ? vs[0] : null;
}

export function shotRecallInfo(shot, stored, matches) {
  const vial = vialForShotAtDate(stored?.vials?.[shot?.compound], shot);
  if (!vial) return null;
  const m = strongestMatchForVial(matches, vial.id);
  if (m) return { source: 'fda', vial, recall: m.recall, match: m.match };
  if (vial.recall) return { source: 'manual', vial, recall: null, match: null };
  return null;
}

// ── Parsing FDA's recalls RSS feed and recall pages (regex-based: Deno has no DOMParser) ──

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', rsquo: "'", lsquo: "'", rdquo: '"', ldquo: '"', ndash: '-', mdash: '-', reg: '®', trade: '™' };
const decodeEntities = s => String(s ?? '')
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
  .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16)))
  .replace(/&([a-z]+);/gi, (m, name) => ENTITIES[name.toLowerCase()] ?? m);
const unCdata = s => String(s ?? '').replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1');
const dropTags = s => s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ');

export function stripTags(html) {
  // Tags are stripped before AND after decoding, so entity-encoded markup (&lt;p&gt;) goes too.
  return dropTags(decodeEntities(dropTags(unCdata(html)))).replace(/\s+/g, ' ').trim();
}

export function toIsoDate(s) {
  const t = String(s ?? '').trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return null;
}

export function recallIdFromUrl(url) {
  return new URL(url).pathname.replace(/\/+$/, '').split('/').pop().toLowerCase();
}

export function parseRss(xml) {
  const items = [];
  for (const m of String(xml ?? '').matchAll(/<item>([\s\S]*?)<\/item>/g)) {
    const tag = t => (m[1].match(new RegExp(`<${t}>([\\s\\S]*?)</${t}>`)) || [])[1] || '';
    const url = stripTags(tag('link')).replace(/^http:/i, 'https:');
    // The link becomes an href in the app, so only ever accept an fda.gov page.
    if (!/^https:\/\/(www\.)?fda\.gov\//i.test(url)) continue;
    const d = new Date(stripTags(tag('pubDate')));
    items.push({
      id: recallIdFromUrl(url), url,
      title: stripTags(tag('title')), description: stripTags(tag('description')),
      publishedAt: isNaN(d.getTime()) ? null : d.toISOString(),
    });
  }
  return items;
}

const lotTokens = text => String(text).split(/[\s,;]+/).map(t => t.replace(/[.)]+$/, '')).filter(t => /\d/.test(t));

export function parseLotTable(html) {
  const src = String(html ?? '');
  const lots = [];
  for (const t of src.matchAll(/<table[\s\S]*?<\/table>/gi)) {
    const rows = [...t[0].matchAll(/<tr[\s\S]*?<\/tr>/gi)].map(r =>
      [...r[0].matchAll(/<t([hd])([^>]*)>([\s\S]*?)<\/t[hd]>/gi)].map(c => ({
        text: stripTags(c[3]),
        rowspan: parseInt((c[2].match(/rowspan="?(\d+)/i) || [])[1], 10) || 1,
      })));
    if (!rows.length) continue;
    const header = rows[0].map(c => c.text.toLowerCase());
    const lotCol = header.findIndex(h => /\blot\b/.test(h));
    if (lotCol < 0) continue;
    const budCol = header.findIndex(h => /beyond use|\bbud\b|exp/.test(h));
    // Cells with rowspan>1 (e.g. Greenwich's Product/Strength/Package Size spanning 10 rows) are
    // absent from later <tr>s, so carry them down by column instead of reading cells by index.
    const carry = [];
    for (const cells of rows.slice(1)) {
      const grid = [];
      let ci = 0;
      for (let col = 0; col < header.length; col++) {
        if (carry[col]?.left > 0) { grid[col] = carry[col].text; carry[col].left--; continue; }
        const cell = cells[ci++];
        if (!cell) break;
        grid[col] = cell.text;
        if (cell.rowspan > 1) carry[col] = { text: cell.text, left: cell.rowspan - 1 };
      }
      const bud = budCol >= 0 ? toIsoDate(grid[budCol]) : null;
      for (const lot of lotTokens(grid[lotCol] || '')) lots.push({ lot, bud });
    }
  }
  if (lots.length) return lots;
  // No usable table: fall back to "Lot: X" / "Lot # X" / "Lot Number X" in the page text.
  const text = stripTags(src);
  for (const m of text.matchAll(/\blot\s*(?:number|no\.?)?\s*[#:]?\s*#?\s*([A-Z0-9][A-Z0-9-]{2,})/gi)) {
    if (/\d/.test(m[1])) lots.push({ lot: m[1], bud: null });
  }
  return lots;
}

function summaryField(html, label) {
  const re = new RegExp(`<dt[^>]*>\\s*${label}:?\\s*</dt>\\s*<dd[^>]*>([\\s\\S]*?)</dd>`, 'i');
  const m = String(html ?? '').match(re);
  if (!m) return '';
  const items = [...m[1].matchAll(/<div class="field--item">([\s\S]*?)<\/div>/g)].map(x => stripTags(x[1])).filter(Boolean);
  return items.length ? items.join(', ') : stripTags(m[1]);
}

export function parseRecallPage(html) {
  return {
    firm: summaryField(html, 'Company Name'),
    brand: summaryField(html, 'Brand Name'),
    product: summaryField(html, 'Product Description'),
    productType: summaryField(html, 'Product Type'),
    reason: summaryField(html, 'Reason for Announcement'),
    lots: parseLotTable(html),
  };
}

// A null/firm-less page means the fetch or parse failed: return null so the caller skips the
// insert and the item is retried on the next run instead of being stored empty forever.
export function buildRecallRow(item, page) {
  if (!page || !page.firm) return null;
  return {
    id: item.id, url: item.url, title: item.title,
    firm: page.firm, brand: page.brand || null, product: page.product || null,
    product_type: page.productType || null, reason: page.reason || null,
    lots: page.lots || [], published_at: item.publishedAt || new Date().toISOString(),
  };
}

export function recallFromRow(row) {
  return {
    id: row.id, url: row.url, title: row.title, firm: row.firm, brand: row.brand || '',
    product: row.product || '', productType: row.product_type || '', reason: row.reason || '',
    lots: Array.isArray(row.lots) ? row.lots : [], publishedAt: row.published_at,
  };
}

export const isRelevantRecall = r => /drug|supplement/i.test(r?.productType || '');
